import type { ApiBody } from '@hello-aria/makalu-api-client'
import { isEmpty, isNil } from 'lodash'

export const accountNumberTypes = ['IBAN', 'ACCOUNT_NUMBER'] as const
export type AccountNumberType = (typeof accountNumberTypes)[number]

export const routingNumberTypes = ['BIC', 'SORT_CODE'] as const
export type RoutingNumberTypes = (typeof routingNumberTypes)[number]

export type PaymentDestination = {
    type: 'BANK_ACCOUNT'
    title?: string
    accountNumberType: 'IBAN' | 'ACCOUNT_NUMBER'
    accountNumber: string
    routingNumberType?: 'BIC' | 'SORT_CODE'
    routingNumber?: string
}

// makalu-api models/PaymentDestination.ts `PaymentDestinationSchema`, the shape a user body accepts.
export type PaymentDestinationBody = NonNullable<
    ApiBody<'/companies/{companyId}/user', 'post'>['paymentDestinations']
>[number]

/*
 * makalu-api accepts a `BANK_ACCOUNT` destination only: an IBAN with an optional BIC, or an account
 * number with its sort code. A destination that fits neither is a bug, so it throws before the request.
 */
export const toPaymentDestinationBody = (destination: PaymentDestination): PaymentDestinationBody => {
    const { title, accountNumber, routingNumber } = destination
    if (destination.accountNumberType === 'IBAN') {
        if (isNil(routingNumber)) return { type: 'BANK_ACCOUNT', title, accountNumberType: 'IBAN', accountNumber }
        return {
            type: 'BANK_ACCOUNT',
            title,
            accountNumberType: 'IBAN',
            accountNumber,
            routingNumberType: 'BIC',
            routingNumber
        }
    }
    if (isNil(routingNumber)) throw new Error('A payment destination with an account number needs its sort code')
    return {
        type: 'BANK_ACCOUNT',
        title,
        accountNumberType: 'ACCOUNT_NUMBER',
        accountNumber,
        routingNumberType: 'SORT_CODE',
        routingNumber
    }
}

export function toPaymentDestination(paymentDestination: Omit<PaymentDestination, 'type'>): PaymentDestination {
    const formattedPaymentDestination: PaymentDestination = {
        ...paymentDestination,
        type: 'BANK_ACCOUNT',
        ...(isEmpty(paymentDestination.routingNumber)
            ? { routingNumberType: undefined, routingNumber: undefined }
            : {
                  routingNumberType: paymentDestination.accountNumberType === 'IBAN' ? 'BIC' : 'SORT_CODE'
              })
    }

    return formattedPaymentDestination
}
